/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import type Store from "@ember-data/store";
import { action } from "@ember/object";
import type RouterService from "@ember/routing/router-service";
import { service } from "@ember/service";
import Component from "@glimmer/component";
import type BioExamResult from "parog-web/models/bio-exam-result";
import type BioExamResultsReceivedJournalEvent from "parog-web/models/bio-exam-results-received-journal-event";
import type BioExamObservation from "parog-web/models/loinc";
import type Patient from "parog-web/models/patient";
import type SemeiaSessionService from "parog-web/services/session";
import { sortBy } from "parog-web/utils/sort";
import triggerFileDownload from "parog-web/utils/trigger-file-download";
import type { ExamsByGroup } from "../utils/groupedBioExams";
import { getGroupedBioExams } from "../utils/groupedBioExams";
// eslint-disable-next-line ember/use-ember-data-rfc-395-imports
import type { DS } from "ember-data";

import styles from "./index.module.css";

type BioExamResultWithJe = BioExamResult & {
  originJournalEvent: BioExamResultsReceivedJournalEvent;
};
type ResultsByLoinc = { [loinc: string]: BioExamResult[] };
type ResultsByLoincChapters = Map<string, ResultsByLoinc>;
type BioExamResultsByGroup = Map<string, ResultsByLoincChapters>;

interface Signature {
  Args: {
    isLoading?: unknown;
    hideAction?: unknown;
    patient: Patient;
  };
}

export default class BioExamResultsSummaryComponent extends Component<Signature> {
  styles = styles;

  @service declare router: RouterService;
  @service declare store: Store;
  @service declare session: SemeiaSessionService;

  get bioExamResultsReceived():
    | BioExamResultsReceivedJournalEvent[]
    | undefined {
    return this.args.patient.bioExamResultsReceivedJournalEvents.sort(
      sortBy("occurredAt", "desc")
    );
  }

  get groupedBioExamObservations(): ExamsByGroup {
    const sortedBioExams = (
      this.store.peekAll("loinc") as DS.RecordArray<BioExamObservation>
    )
      // @ts-expect-error the return type of this.store.peekAll provided by @types/ember-data is not correct
      .toSorted(function (bioA: BioExamObservation, bioB: BioExamObservation) {
        return bioA.rank - bioB.rank;
      }) as BioExamObservation[];

    return getGroupedBioExams(sortedBioExams);
  }

  /**
   * Each entry in the Object represents an exam group, with a group name and a `codes` object
   * Each entry in the `codes` object represents a row of the summary table
   * The keys are the codes for the rows
   * the values are an array of BioExamResult
   */
  get resultsByExamGroups(): BioExamResultsByGroup {
    const resultsByExamGroups = new Map();

    if (!this.bioExamResultsReceived)
      return resultsByExamGroups as BioExamResultsByGroup;

    this.groupedBioExamObservations.forEach((group, name) => {
      if (group.size == 0) return;

      if (!resultsByExamGroups.has(name)) {
        resultsByExamGroups.set(name, new Map<string, ResultsByLoinc>());
      }

      group.forEach((observations, loincChapter) => {
        const resultsByLoinc: ResultsByLoinc = observations.reduce(
          (acc, obs) => {
            const code = obs.loinc;
            if (
              !(
                this
                  .bioExamResultsReceived as BioExamResultsReceivedJournalEvent[]
              ).find(je => je.result(`LOINC-${code}`))
            )
              return acc;

            acc[code] = (
              this
                .bioExamResultsReceived as BioExamResultsReceivedJournalEvent[]
            ).map(je => this.resultWithJe(je, code));

            return acc;
          },
          {}
        );

        if (!Object.keys(resultsByLoinc).length) return;

        resultsByExamGroups.get(name).set(loincChapter, resultsByLoinc);
      });
    });
    return resultsByExamGroups;
  }

  @action
  downloadSummary() {
    const dates = [""];
    const rows: string[][] = [];

    document
      .querySelectorAll(
        "#bio-summary-table > thead > tr > th[data-test-column]"
      )
      .forEach(thElement => {
        dates.push(
          (thElement as HTMLTableCellElement).innerText.split("\n")[0]
        );
      });

    rows.push(dates);

    let group = "";

    document.querySelectorAll("table > tbody > tr").forEach(trElement => {
      const rowContent: string[] = [];
      // Get the LOINC group value from the tr element
      if (trElement.attributes.getNamedItem("data-test-group")) {
        group = (trElement.attributes.getNamedItem("data-test-group") as Attr)
          .value;
      }
      // Get the LOINC chapter from the tr element
      if (trElement.attributes.getNamedItem("data-test-loinc-chapter-row")) {
        let loincChapter = (
          trElement.children[0] as HTMLTableCellElement
        ).innerText.trim();

        // Append the LOINC group to the LOINC Chapter
        switch (group) {
          // Ex : Hématologie sanguine
          case "blood":
            loincChapter += " sanguine";
            break;
          // Ex : Biochimie urinaire
          case "urinary":
            loincChapter += " urinaire";
            break;
          // Ex : Autres réultats - Fertilité
          default:
            loincChapter = `Autres résultats - ${loincChapter}`;
        }
        rowContent.push(loincChapter);
      }
      // Get the result value & unit from the tr element
      if (trElement.attributes.getNamedItem("data-test-results-row")) {
        const children = trElement.children;
        for (let i = 0; i < children.length; i++) {
          const child = children.item(i) as HTMLTableCellElement;
          rowContent.push(child.innerText.replace("\n", " ").trim());
        }
      }

      if (rowContent.length) rows.push(rowContent);
    });

    triggerFileDownload({
      fileContent:
        "data:text/csv;charset=utf-8," + rows.map(e => e.join(",")).join("\n"),
      fileName: `historique_bilans_biologie_patient_${this.args.patient.fullName.replace(
        " ",
        "_"
      )}.csv`
    });
  }

  private resultWithJe(
    je: BioExamResultsReceivedJournalEvent,
    code: string
  ): BioExamResultWithJe | undefined {
    const result = je.result(`LOINC-${code}`);
    if (result) {
      (result as BioExamResultWithJe).originJournalEvent = je;
    }
    return result as BioExamResultWithJe;
  }
}
